import { useState, useEffect, useCallback } from 'react';
import analyticsService from 'services/analyticsService';
import { MatchAnalytics } from 'types/analytics';

interface UseMatchAnalyticsState {
    data: MatchAnalytics | null;
    loading: boolean;
    error: string | null;
    refresh: () => void;
}

export const useMatchAnalytics = (matchId: string): UseMatchAnalyticsState => {
    const [data, setData] = useState<MatchAnalytics | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [etag, setEtag] = useState<string | null>(null);
    const [refreshIndex, setRefreshIndex] = useState(0);

    const fetchData = useCallback(async () => {
        if (!matchId) {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await analyticsService.fetchMatchAnalytics(matchId, etag || undefined);
            setData(result.data);
            setEtag(result.etag);
        } catch (err: any) {
            if (err.response?.status === 304) {
                // Not modified - data is already cached
                return;
            }
            setError(err.response?.data?.error || err.message || 'Failed to load match analytics');
        } finally {
            setLoading(false);
        }
    }, [matchId, etag]);

    useEffect(() => {
        fetchData();
    }, [fetchData, refreshIndex]);

    const refresh = useCallback(() => {
        setEtag(null); // Clear etag to force refresh
        setRefreshIndex((prev) => prev + 1);
    }, []);

    return {
        data,
        loading,
        error,
        refresh,
    };
};

export default useMatchAnalytics;






